import React from "react";
import {
  CalendarIcon,
  UsersIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import { defaultpfp, generations } from "../../constants/constants";

interface ProfileHeaderProps {
  profile: {
    id: string;
    display_name: string;
    username: string;
    bg_color: string;
    createdAt: string;
    profileImg?: string;
    generation?: number;
  };
  stats: {
    followersCount: number;
    followingCount: number;
    listsCount: number;
    goatScore: number;
  };
  isOwnProfile: boolean;
  isFollowing?: boolean;
  followLoading?: boolean;
  onFollow: () => void;
  onCompareLists: () => void;
  onShareProfile: () => void;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  profile,
  stats,
  isOwnProfile,
  isFollowing = false,
  followLoading = false,
  onFollow,
  onShareProfile,
}) => {
  const formatJoinDate = (dateString: string) => {
    if (!dateString) return "Unknown";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return "Unknown";
    }

    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };
  console.log("ProfileHeader stats", profile);

  return (
    <div className=" p-6 mb-6">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
        {/* Avatar */}
        <div className="relative w-24 h-24 rounded-full flex-shrink-0 overflow-hidden">
          {profile.profileImg ? (
            <img
              src={profile.profileImg}
              alt={`${profile.display_name}'s profile`}
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={defaultpfp}
              alt={`${profile.display_name}'s profile`}
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Profile Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">
                {profile.display_name}
              </h1>
              {profile.generation && generations[profile.generation - 1] && (
                <span className="inline-block text-xs font-bold uppercase tracking-wider bg-blue-600/20 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded-full mb-1">
                  {generations[profile.generation - 1]}
                </span>
              )}
              <p className="text-gray-400 flex items-center">
                <CalendarIcon className="w-4 h-4 mr-1" />
                Joined {formatJoinDate(profile.createdAt)}
              </p>
            </div>

            {/* Action Buttons */}
            {!isOwnProfile && (
              <div className="flex gap-3 mt-4 md:mt-0">
                <button
                  onClick={onFollow}
                  disabled={followLoading}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-60 ${
                    isFollowing
                      ? "bg-gray-600 hover:bg-gray-700 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {followLoading ? "..." : isFollowing ? "Following" : "Follow"}
                </button>
                {/* <button
                  onClick={onCompareLists}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Compare Lists
                </button> */}
                <button
                  onClick={onShareProfile}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Share Profile
                </button>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center md:text-left">
              <div className="flex items-center text-gray-300">
                <UsersIcon className="w-4 h-4 mr-1" />
                <span className="text-sm">Followers</span>
              </div>
              <div className="text-xl font-bold text-white">
                {stats.followersCount.toLocaleString()}
              </div>
            </div>

            <div className="text-center md:text-left">
              <div className="flex items-center text-gray-300">
                <UsersIcon className="w-4 h-4 mr-1" />
                <span className="text-sm">Following</span>
              </div>
              <div className="text-xl font-bold text-white">
                {stats.followingCount.toLocaleString()}
              </div>
            </div>

            <div className="text-center md:text-left">
              <div className="flex items-center text-gray-300">
                <ListBulletIcon className="w-4 h-4 mr-1" />
                <span className="text-sm">Lists</span>
              </div>
              <div className="text-xl font-bold text-white">
                {stats.listsCount}
              </div>
            </div>

            {/* <div className="text-center md:text-left">
              <div className="flex items-center text-gray-300">
                <TrophyIcon className="w-4 h-4 mr-1" />
                <span className="text-sm">GOAT Score</span>
              </div>
              <div className="text-xl font-bold text-yellow-400">
                {stats.goatScore}
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
